// Durable partner-intent flag. Set on `/select-role` when a user picks
// "Airbnb Host" or "Food or Drink Venue" and consumed after the user
// verifies their email (or on Capacitor cold-relaunch mid-onboarding) to
// route them into the correct partner flow instead of dumping them
// into the Guest app.
//
// Stored in localStorage — NOT sessionStorage — because Capacitor Android
// wipes sessionStorage on background/foreground cycles which is exactly
// how the 3am "picked partner, landed in guest app" bug happened.
//
// Priority is ALWAYS: deep-link invite params (host invite / venue
// referral / staff invite / guest invite) > partner intent > default guest
// home. Deep-link handlers set no partner intent, so a guest-invite deep
// link still routes normally.

const KEY = 'pendingPartnerIntent';
// Separate marker recording that the user has explicitly chosen a role
// (guest/host/venue) on /select-role at least once. Used to prevent the
// picker from re-appearing for existing plain guests on every cold-launch
// of '/'. `pendingPartnerIntent` alone can't do this job because the
// intent is cleared once onboarding lands the user in their app shell.
const CHOSEN_KEY = 'allureRoleChosen';
const VALID = new Set(['guest', 'host', 'venue']);

export const setPendingPartnerIntent = (intent) => {
  if (!VALID.has(intent)) return;
  try { localStorage.setItem(KEY, intent); } catch { /* noop */ }
  // Any card tap on /select-role also counts as "role explicitly chosen".
  try { localStorage.setItem(CHOSEN_KEY, '1'); } catch { /* noop */ }
};

export const getPendingPartnerIntent = () => {
  try {
    const value = localStorage.getItem(KEY);
    return VALID.has(value) ? value : '';
  } catch {
    return '';
  }
};

export const clearPendingPartnerIntent = () => {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
  // Also clear the legacy sessionStorage/localStorage key from the previous
  // partner-picker flow so a stale value can't route a fresh signup back
  // through the retired /switch-role middle-state.
  try { localStorage.removeItem('selectedRoleType'); } catch { /* noop */ }
  try { sessionStorage.removeItem('selectedRoleType'); } catch { /* noop */ }
};

export const hasChosenRole = () => {
  try {
    return localStorage.getItem(CHOSEN_KEY) === '1';
  } catch {
    return false;
  }
};

export const markRoleChosen = () => {
  try { localStorage.setItem(CHOSEN_KEY, '1'); } catch { /* noop */ }
};

export const clearRoleChosen = () => {
  try { localStorage.removeItem(CHOSEN_KEY); } catch { /* noop */ }
};

// True when a logged-in user has NOT yet made an explicit role choice —
// i.e. they registered directly at /register (or via Google) without
// ever going through the /select-role picker AND have never tapped a
// card on /select-role. Such users must land on /select-role after
// verifying email so they pick Guest / Host / Venue instead of getting
// silently dropped into the guest app. Users who already have a partner
// status (host_status, venue_status), admins/staff, users whose backend
// role is no longer `guest`, OR anyone who has already tapped a role
// card at least once (allureRoleChosen === '1') have already been
// routed past this gate and get their normal default route. Deep-link
// invitees always bypass this check upstream.
// True when the account was created very recently — used to detect a
// brand-new signup versus an existing guest logging in on any device.
// The picker should only be forced for brand-new signups.
const FRESH_ACCOUNT_WINDOW_MS = 30 * 60 * 1000; // 30 minutes
const isFreshAccount = (user) => {
  if (!user) return false;
  const raw = user.created_at || user.createdAt;
  if (!raw) return true; // no timestamp → be safe and treat as fresh
  const created = Date.parse(raw);
  if (Number.isNaN(created)) return true;
  return (Date.now() - created) < FRESH_ACCOUNT_WINDOW_MS;
};

export const shouldForceRolePicker = (user) => {
  if (!user) return false;
  if (user.role !== 'guest') return false;
  if (user.host_status) return false;
  if (user.venue_status) return false;
  // An existing guest who is simply logging back in is NEVER forced to
  // re-pick their role — the picker exists only for brand-new signups.
  // Any account whose `created_at` is more than 30 minutes ago is
  // treated as "existing" regardless of what's in device localStorage
  // (which can leak across accounts on shared devices — iter 305 bug).
  if (!isFreshAccount(user)) return false;
  // Fresh account: only skip the picker once the user has explicitly
  // tapped a card on /select-role during this signup flow.
  if (hasChosenRole()) return false;
  return true;
};
